import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaTwitter, FaLinkedin, FaFacebook, FaLink } from 'react-icons/fa';
import TypeAvatar from './TypeAvatar';
import { buildTypeCode, buildShareHeadline } from '../utils/founderIdentity';
import { getFounderType } from '../data/founderTypes';
import { trackEvent } from '../utils/analytics';
import { SHARE } from '../data/productCopy';

const ShareContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 2rem;
  width: 100%;
`;

const ShareTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: var(--text-primary);
`;

const ShareCard = styled.div`
  width: 100%;
  max-width: 720px;
  background: linear-gradient(
    145deg,
    rgba(18, 18, 28, 0.95) 0%,
    rgba(18, 18, 28, 0.75) 100%
  );
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  padding: 1.5rem;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.25);
  margin-bottom: 1.25rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${(props) => props.$accent || 'var(--primary-color)'};
  }
`;

const ShareCardTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;

const ShareCardBrand = styled.div`
  font-weight: 700;
  font-size: 1.05rem;
  color: var(--text-primary);

  span {
    background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const ShareCardMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  text-align: right;
`;

const BlendLine = styled.p`
  margin: 0;
  color: var(--text-primary);
  font-weight: 600;
  font-size: 1rem;
`;

const InsightLine = styled.p`
  margin: 0.5rem 0 0;
  color: var(--text-secondary);
  line-height: 1.5;
  font-size: 0.98rem;
`;

const ShareCardFooter = styled.div`
  margin-top: 0.75rem;
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.9rem;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const ShareButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${props => props.bgColor || 'rgba(255, 255, 255, 0.1)'};
  color: var(--text-primary);
  font-size: 1.2rem;
  transition: all 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.15);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:focus-visible {
    outline: 2px solid rgba(var(--primary-rgb), 0.6);
    outline-offset: 3px;
  }
`;

const CopyLinkContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: 400px;
  position: relative;
`;

const LinkInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  padding-right: 3.5rem;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: var(--border-radius);
  color: var(--text-primary);
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary-light);
  }
`;

const CopyButton = styled(motion.button)`
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  padding: 0.5rem;
  background: var(--primary-dark);
  color: white;
  border-radius: var(--border-radius);
  font-size: 0.8rem;
  font-weight: 500;

  &:focus-visible {
    outline: 2px solid rgba(var(--primary-rgb), 0.6);
    outline-offset: 2px;
  }
`;

const CopiedMessage = styled(motion.div)`
  position: absolute;
  bottom: -25px;
  left: 0;
  width: 100%;
  text-align: center;
  font-size: 0.8rem;
  color: var(--secondary-color);
`;

const getFallbackShareLink = () => `${window.location.origin}/`;

const buildBlendText = ({ primaryTitle, primaryPercent, secondaryTitle, secondaryPercent }) => {
  const parts = [];
  if (primaryTitle && typeof primaryPercent === 'number') {
    parts.push(`${primaryPercent}% ${primaryTitle}`);
  }
  if (secondaryTitle && typeof secondaryPercent === 'number') {
    parts.push(`${secondaryPercent}% ${secondaryTitle}`);
  }
  return parts.length > 0 ? parts.join(', ') : '';
};

const ShareResults = ({
  primaryTitle,
  primaryPercent,
  secondaryTitle,
  secondaryPercent,
  oneLineInsight,
  profileType,
  shareUrl,
  primaryTypeId,
  secondaryTypeId,
}) => {
  const [copied, setCopied] = useState(false);
  
  const shareLink = shareUrl || getFallbackShareLink();
  const primaryType = primaryTypeId ? getFounderType(primaryTypeId) : null;
  const typeCode = buildTypeCode(primaryTypeId, secondaryTypeId);
  const blendText = buildBlendText({ primaryTitle, primaryPercent, secondaryTitle, secondaryPercent });
  const shareHeadline = buildShareHeadline({
    primaryType: primaryTypeId,
    secondaryType: secondaryTypeId,
  }) || (blendText ? `My founder type: ${blendText}` : `My founder type: ${profileType || 'Founder'}`);
  const shareFocus = oneLineInsight ? `This week: ${oneLineInsight}` : '';
  const shareText = shareFocus ? `${shareHeadline}. ${shareFocus}.` : `${shareHeadline}.`;
  
  // Social share URLs
  const twitterShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText + ' Take the quiz:')}&url=${encodeURIComponent(shareLink)}`;
  const linkedinShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareLink)}`;
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink)}`;
  
  // Copy link to clipboard
  const copyToClipboard = () => {
    const payload = `${shareText}\n${shareLink}`;
    navigator.clipboard.writeText(payload).then(() => {
      trackEvent('share_clicked', { method: 'copy', destination: 'clipboard' });
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  };
  
  return (
    <ShareContainer>
      <ShareTitle>{SHARE.title}</ShareTitle>

      <ShareCard role="group" aria-label="Share card preview" $accent={primaryType?.color}>
        <ShareCardTopRow>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {primaryType && <TypeAvatar type={primaryType} size={56} animate={false} />}
            <div>
              <ShareCardBrand>
                Founder<span>DNA</span>
              </ShareCardBrand>
              {typeCode && (
                <div style={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.15em', color: primaryType?.color, marginTop: '0.25rem' }}>
                  {typeCode}
                </div>
              )}
              <BlendLine>{blendText || profileType || 'Founder'}</BlendLine>
            </div>
          </div>
          <ShareCardMeta>
            <span style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '0.9rem' }}>{SHARE.cardLabel}</span>
          </ShareCardMeta>
        </ShareCardTopRow>

        {primaryType?.tagline && (
          <InsightLine style={{ fontStyle: 'italic', marginTop: '0.75rem' }}>{primaryType.tagline}</InsightLine>
        )}
        {oneLineInsight && <InsightLine><strong>This week:</strong> {oneLineInsight}</InsightLine>}
        <ShareCardFooter>{SHARE.footer}</ShareCardFooter>
      </ShareCard>
      
      <ButtonsContainer>
        <ShareButton 
          as="a" 
          href={twitterShareUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          bgColor="#1DA1F2"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Share on X"
          onClick={() => trackEvent('share_clicked', { method: 'social', destination: 'x' })}
        >
          <FaTwitter />
        </ShareButton>
        
        <ShareButton 
          as="a" 
          href={linkedinShareUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          bgColor="#0077B5"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Share on LinkedIn"
          onClick={() => trackEvent('share_clicked', { method: 'social', destination: 'linkedin' })}
        >
          <FaLinkedin />
        </ShareButton>
        
        <ShareButton 
          as="a" 
          href={facebookShareUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          bgColor="#4267B2"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Share on Facebook"
          onClick={() => trackEvent('share_clicked', { method: 'social', destination: 'facebook' })}
        >
          <FaFacebook />
        </ShareButton>
        
        <ShareButton 
          onClick={copyToClipboard}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Copy share text and link"
          type="button"
        >
          <FaLink />
        </ShareButton>
      </ButtonsContainer>
      
      <CopyLinkContainer>
        <LinkInput 
          type="text" 
          value={`${shareText} ${shareLink}`.trim()} 
          readOnly 
          onClick={(e) => e.target.select()}
          aria-label="Share text and link"
        />
        <CopyButton
          onClick={copyToClipboard}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          type="button"
        >
          {copied ? 'Copied!' : 'Copy'}
        </CopyButton>
        
        {copied && (
          <CopiedMessage
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            Link copied to clipboard!
          </CopiedMessage>
        )}
      </CopyLinkContainer>
    </ShareContainer>
  );
};

export default ShareResults; 